import { guestPassDisplay } from "./guestPass";

export const formatPhone = (phone) => {
  const value = phone ? String(phone) : "";
  const cleaned = value.replace(/\D/g, "");
  if (cleaned.length !== 10) return value;
  return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
};

export const isWithin30Days = (createdAt) => {
  if (!createdAt) return false;
  const diffInDays = (new Date() - new Date(createdAt)) / (1000 * 60 * 60 * 24);
  return diffInDays <= 30;
};

const isTruthyFlag = (value) => Number(value) === 1 || value === true || value === "true";

// Photos from the API: [{ id, url, thumb }] (strings are accepted too, e.g. signed request photo urls)
export const normalizePhotos = (photos) => {
  if (!Array.isArray(photos)) return [];
  return photos
    .map((photo) => {
      if (!photo) return null;
      if (typeof photo === "string") return { id: null, url: photo, thumb: photo };
      const url = photo.url || photo.src || photo.path || photo.thumb || "";
      if (!url) return null;
      return { id: photo.id ?? null, url, thumb: photo.thumb || photo.thumbnail || url };
    })
    .filter(Boolean);
};

// Photos to show for a row: the photos list, or the legacy single image of older vehicles
export const rowPhotos = (row) => {
  if (row?.photos?.length > 0) return row.photos;
  if (row?.image_path) return [{ id: null, url: row.image_path, thumb: row.image_thumbail_path || row.image_path }];
  return [];
};

export const rowThumb = (row) => row?.photos?.[0]?.thumb || row?.photos?.[0]?.url || row?.image_thumbail_path || row?.image_path || "";

const pluralDays = (days) => `${days} day${Number(days) === 1 ? "" : "s"}`;

// Approval state of a row: pending | approved | rejected | disabled
export const getApproval = (row) => {
  const approval = row?.approval || {};
  const state = approval.state || (row?.disabled ? "disabled" : "approved");
  const days = approval.days ?? null;
  const defaults = {
    pending: days !== null ? `Pending · ${pluralDays(days)}` : "Pending",
    approved: "Approved",
    rejected: "Rejected",
    disabled: "Disabled",
  };
  return {
    state,
    type: approval.type || null,
    label: approval.label || defaults[state] || state,
    sub: approval.sub || "",
    by: approval.by || "",
    at: approval.at || null,
    days,
    reviewNote: approval.review_note || "",
    source: approval.source || "",
  };
};

export const pendingUpdateLabel = (row) => {
  const days = row?.pending_update?.days;
  return days !== null && days !== undefined ? `Update pending · ${pluralDays(days)}` : "Update pending";
};

// The vehicle request waiting for review (a new request, or the update request of an approved vehicle)
export const getRequestId = (row) => row?.request_id || row?.pending_update?.request_id || (row?.kind === "request" ? row?.id : null);

export const isVehicleRow = (row) => row?.kind !== "request";

// Rejected new vehicle / guest (a request row): staff can correct it or remove it from the list
export const isRejectedRequest = (row) => row?.kind === "request" && getApproval(row).state === "rejected";

/*
 * One row of v1/vehicles/list or v1/vehicles/grid, mapped for the list, grid and mobile views.
 * kind 'request' rows are new vehicles waiting for approval (id is the request id); key is `${kind}-${id}`.
 */
export const mapVehicleRow = (item) => {
  const isGuest = isTruthyFlag(item?.is_guest);
  const kind = item?.kind === "request" ? "request" : "vehicle";
  const fname = item?.guest_fname || "";
  const lname = item?.guest_lname || "";

  const common = {
    key: `${kind}-${item?.id}`,
    id: item?.id,
    kind,
    vehicle_id: item?.vehicle_id ?? (kind === "vehicle" ? item?.id : null),
    request_id: item?.request_id ?? null,
    approval: item?.approval || null,
    pending_update: item?.pending_update || null,
    photos: normalizePhotos(item?.photos),
    disabled: isTruthyFlag(item?.disabled),
    gate_access: item?.gate_access || null,
    other_vehicles_in_unit: Array.isArray(item?.other_vehicles_in_unit) ? item.other_vehicles_in_unit : [],
    history: item?.history || "",
    created_at: item?.created_at,
    license: item?.license,
    make: item?.make,
    model: item?.model,
    color: item?.color,
    property_id: item?.property_id,
    property: item?.property_name,
    unit: item?.apt_no,
    is_disabled: item?.is_disabled,
    tenant_id: item?.tenant_id,
    tenant_info_id: item?.tenant_info_id,
    tenant_name: item?.tenant_name || "",
    apt_id: item?.apt_id,
    isNew: isWithin30Days(item?.created_at),
    image_path: item?.image_path || "",
    image_thumbail_path: item?.image_thumbail_path || "",
  };

  if (isGuest) {
    return {
      ...common,
      initials: fname && lname ? `${fname[0]}${lname[0]}`.toUpperCase() : "G",
      name: `${fname} ${lname}`.trim(),
      phone: formatPhone(item?.phone || item?.guest_phone || ""),
      tenant_image: "",
      is_guest: 1,
      // Latest parking pass from the server: raw and display values
      guest_pass: item?.guest_pass || null,
      pass: guestPassDisplay(item?.guest_pass),
      guest_fname: fname,
      guest_lname: lname,
      guest_phone: item?.guest_phone || "",
      tenant_details: null,
    };
  }

  return {
    ...common,
    initials: item?.tenant_initial || (item?.tenant_name || "?").slice(0, 1).toUpperCase(),
    name: item?.tenant_name || "",
    phone: formatPhone(item?.phone || ""),
    tenant_image: item?.tenant_image || "",
    tenant_image2: item?.tenant_image2 || "",
    is_guest: 0,
    tenant_details: { ...(item?.tenant_details || {}), tenant_image: item?.tenant_image, property: item?.property_name, unit: item?.apt_no },
  };
};
